import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertTitle,
  Box,
  Link,
  Alert as MuiAlert,
  AlertProps as MuiAlertProps,
  Stack,
  SxProps,
  Theme,
} from '@mui/material';
import { useLocalStorage } from 'common/hooks/useLocalStorage';
import { VISUAL_REFRESH, isExternalUrl } from 'common/utils';
import { Alert as DsAlert } from 'components';

import { Button } from '../Buttons/Button';
import { toDsProps } from './toDsProps';

import type { AlertAction } from './types';

export type AlertProps = {
  id?: string;
  title?: string;
  variant?: MuiAlertProps['severity'];
  description?: ReactNode;
  children?: ReactNode | undefined;
  actions?: AlertAction[];
  /** MUI spacing between action buttons. Defaults to 3 (24px). */
  actionsGap?: number;
  sx?: SxProps<Theme>;
  Icon?: ReactNode;
  dismissible?: boolean;
  /**
   * ARIA role override. Defaults to MUI's `'alert'` (assertive). Pass `'status'` for non-critical,
   * informational messages (e.g. a cooldown notice) so screen readers announce them politely.
   */
  role?: MuiAlertProps['role'];
  /**
   * `aria-live` override. Leave undefined to inherit the politeness implied by `role`; set to
   * `'polite'` alongside `role="status"` to be explicit for assistive technologies that don't map
   * the role to a live-region politeness on their own.
   */
  'aria-live'?: React.AriaAttributes['aria-live'];
} & Pick<MuiAlertProps, 'onClose'>;

/**
 * The refreshed path's actions, drawn with the legacy `Button` so they follow the same flag: under
 * the refresh it renders the DS button (`medium` -> `xs`, 32px), and a variant it cannot map falls
 * back to MUI on its own.
 *
 * A link action is an outlined link-button (`outlined` -> DS `primary`/`outline`) rather than MUI's
 * inline text link, which the DS set has no counterpart for. It keeps the MUI link's blue. Not the
 * borderless `text` cell: its inline padding pushes the label off the message's left edge and it
 * does not read as clickable. Not `muted`/`outline` either: its border is the neutral panel's own
 * border colour, so on the commonest severity it all but disappears. An internal path goes through
 * the router; an external URL opens a new tab, as the MUI path's `Link` does.
 */
const renderDsActions = (actions: AlertAction[]) =>
  actions.map(({ label, href, onClick, variant }, index) => (
    <Button
      key={index}
      size="medium"
      variant={href ? (variant ?? 'outlined') : variant}
      onClick={onClick}
      {...(href ? (isExternalUrl(href) ? { externalHref: href } : { href }) : {})}
    >
      {label}
    </Button>
  ));

export const Alert = ({
  id,
  variant = 'white',
  actions = [],
  actionsGap = 3,
  title,
  description,
  children,
  onClose,
  sx,
  Icon,
  dismissible,
  role,
  'aria-live': ariaLive,
}: AlertProps) => {
  const { data, storeData } = useLocalStorage<boolean>(`alert-${id}`);
  const { t } = useTranslation('common');

  const handleClose = useCallback(
    (event: React.SyntheticEvent) => {
      if (dismissible) {
        storeData(true);
      }
      onClose?.(event);
    },
    [dismissible, onClose, storeData]
  );

  /**
   * The DS alert's `onDismiss` takes no argument, so this cannot be `handleClose` — and it does not
   * need to be, because `toDsProps` refuses `dismissible` + `onClose` outright. The only thing left
   * to do is remember the dismissal, which is what makes the panel below unmount on the next render
   * and stay gone on the next visit.
   */
  const handleDismiss = useCallback(() => {
    storeData(true);
  }, [storeData]);

  /**
   * Sits above BOTH implementations, where it used to sit between them: a dismissed alert renders
   * nothing whichever one would have drawn it. It could live below the DS branch only while
   * `toDsProps` refused every dismissible alert, which it stopped doing in HNS-339.
   */
  const isDismissed = dismissible && data;

  if (isDismissed) {
    return null;
  }

  /**
   * The visual-refresh gate lives HERE, not at the call sites — all ~70 of them keep this
   * component's MUI-shaped API and never mention the flag. `toDsProps` returns `null` for anything
   * DS 2.0 cannot express, which falls through to the MUI implementation below.
   *
   * Declared after the hooks above so they stay unconditional.
   */
  const dsProps = VISUAL_REFRESH
    ? toDsProps({ variant, title, description, children, Icon, dismissible, onClose, role })
    : null;

  if (dsProps) {
    /**
     * `description` and `children` collapse into the DS alert's single body slot. The wrapper has
     * always rendered both, one after the other, and only a handful of call sites pass both.
     *
     * `id` carries through so any external `aria-describedby`/label reference still resolves. The
     * wrapper's own `aria-labelledby` is dropped on purpose: it pointed at the MUI title node, and
     * the DS panel's `status`/`alert` role already announces the title as part of its content.
     */
    const shared = {
      ...dsProps,
      actions: actions.length ? renderDsActions(actions) : undefined,
      'aria-live': ariaLive,
      'data-testid': `alert-${variant}`,
      id,
    };

    const body = (
      <>
        {description}
        {children}
      </>
    );

    /**
     * Two call forms rather than one with a conditional spread, because `AlertProps` pairs
     * `onDismiss` and `dismissLabel` in a union so that one cannot be passed without the other.
     * Anything that makes the pair optional — a `| undefined` variable, or `dismissible && {...}` —
     * widens both to optional and satisfies neither branch of that union. Passing them literally is
     * what proves to the compiler they arrive together.
     *
     * The label names the alert it dismisses rather than saying "Dismiss", since a page can carry
     * more than one: the wizard's Tags step renders its guide alongside a `maxCategoriesReached`
     * alert. Falls back to the generic string for a caller with no title.
     */
    const panel = dismissible ? (
      <DsAlert
        {...shared}
        dismissLabel={title ? t('dismissNamedAlert', { title }) : t('dismissAlert')}
        onDismiss={handleDismiss}
      >
        {body}
      </DsAlert>
    ) : (
      <DsAlert {...shared}>{body}</DsAlert>
    );

    // Why a box is enough for `sx`: see `toDsProps`. No box without one.
    return sx ? <Box sx={sx}>{panel}</Box> : panel;
  }

  return (
    <MuiAlert
      data-testid={`alert-${variant}`}
      variant="standard"
      severity={variant}
      sx={sx}
      icon={Icon}
      role={role}
      aria-live={ariaLive}
      onClose={dismissible ? handleClose : undefined}
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      {title && <AlertTitle id={`${id}-title`}>{title}</AlertTitle>}
      {description}
      {children}
      {!!actions.length && (
        <Stack direction="row" flexWrap="wrap" rowGap={1} gap={actionsGap} mt={2}>
          {actions.map(({ label, href, onClick, variant }, index) =>
            href ? (
              <Link
                key={index}
                component="a"
                href={href}
                {...(isExternalUrl(href) && { target: '_blank', rel: 'noopener noreferrer' })}
                variant="natter-text-sm"
              >
                {label}
              </Link>
            ) : (
              <Button key={index} size="medium" variant={variant} onClick={onClick}>
                {label}
              </Button>
            )
          )}
        </Stack>
      )}
    </MuiAlert>
  );
};
